/**
 * Theme resolution + application — the single source of truth for how PSPulse
 * chooses between its two first-class, individually-curated themes (DESIGN.md:
 * light and dark are NOT an inverted toggle).
 *
 * The user's choice is one of three:
 *   - "light" / "dark"  — explicit, wins over the OS preference.
 *   - "system"          — follow `prefers-color-scheme`, live (no reload).
 * Unset / malformed / unreadable storage ⇒ "system" (never throw).
 *
 * ── Mechanism (A): system == ABSENCE of the `data-theme` attribute ──────────
 * We drive the two themes through CSS thus:
 *   - explicit dark  → `[data-theme="dark"]` token blocks in index.css.
 *   - explicit light → base `:root` tokens (attribute present ⇒ the @media
 *     dark blocks, scoped to `:not([data-theme])`, no longer match — so
 *     explicit Light correctly beats an OS-dark preference).
 *   - system         → NO attribute, so `@media (prefers-color-scheme: dark)`
 *     (scoped to `html:not([data-theme])`) drives the dark tokens AND
 *     live-updates on an OS flip for free — no JS repaint needed for tokens.
 * The only things JS must keep in sync in system mode are the `color-scheme`
 * property (for the pre-CSS canvas/scrollbar/form paint) and the `theme-color`
 * meta — both keyed to the RESOLVED theme; see `applyTheme` + the inline
 * no-FOUC script in index.html, which mirror this exact logic.
 */

export const THEME_STORAGE_KEY = "pspulse-theme";

/** What the user picked. "system" follows the OS `prefers-color-scheme`. */
export type ThemeChoice = "light" | "dark" | "system";

/** The concrete theme actually painted (system resolved against the OS). */
export type ResolvedTheme = "light" | "dark";

/** Browser-chrome tint per resolved theme — the warm paper / dark base from the
 *  DESIGN.md palette. Kept here so the meta tag and the inline script agree. */
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: "#fbf8f6",
  dark: "#161318",
};

/** Narrowing type-guard: is an arbitrary value a valid stored choice? */
export function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === "light" || value === "dark" || value === "system";
}

/**
 * Read the persisted choice. Unset, malformed, or an unreadable Storage (e.g.
 * privacy mode throwing on access) all fall back to "system" — never throws.
 */
export function readStoredTheme(storage?: Storage): ThemeChoice {
  try {
    const raw = (storage ?? window.localStorage).getItem(THEME_STORAGE_KEY);
    return isThemeChoice(raw) ? raw : "system";
  } catch {
    return "system";
  }
}

/** Persist the choice. Swallows storage errors (never throws). */
export function persistTheme(choice: ThemeChoice, storage?: Storage): void {
  try {
    (storage ?? window.localStorage).setItem(THEME_STORAGE_KEY, choice);
  } catch {
    /* storage unavailable — the in-memory choice still applies this session */
  }
}

/** Resolve a choice to the concrete theme, given the OS dark preference. */
export function resolveTheme(choice: ThemeChoice, prefersDark: boolean): ResolvedTheme {
  if (choice === "system") return prefersDark ? "dark" : "light";
  return choice;
}

/** Does the OS currently prefer dark? Safe when matchMedia is unavailable. */
export function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Apply a choice to the live document (see the mechanism note above):
 *   - system  ⇒ remove `data-theme` (let the scoped @media rules drive tokens).
 *   - explicit ⇒ set `data-theme` so the explicit blocks win over the OS pref.
 *   - always set `color-scheme` + the `theme-color` meta to the RESOLVED theme.
 * No-op outside a browser.
 */
export function applyTheme(choice: ThemeChoice): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  const resolved = resolveTheme(choice, systemPrefersDark());

  if (choice === "system") {
    el.removeAttribute("data-theme");
  } else {
    el.setAttribute("data-theme", choice);
  }
  el.style.colorScheme = resolved;

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLORS[resolved]);
}
